import { DateTime } from "luxon";

import { config } from "@/Common/envConfig";
import type { SendVideoCallLogsApiQuery } from "@/Modules/Models/Api/VideoCalls";
import type { SendVideoCallLogsQuery, VideoCallPayload } from "@/Modules/Models/Domain/VideoCalls";
import type { PublisherStatContainer } from "@opentok/client";
import { type VideoCallStore } from "../Store";

import { sendLogs } from "@/Services/TherapyApi/VideoCalls";

import { log } from "@/Utils/Debug";
import { snake } from "@/Utils/Snakelize";
import { isApp, isIosApp } from "@/Utils/Ionic";

enum VideoCallStatus {
  OK = "OK",
  KO = "KO",
  ERROR = "ERROR",
}

const transformLogsQueryToLogsQueryApi = (
  sendVideoCallLogsQuery: SendVideoCallLogsQuery
): SendVideoCallLogsApiQuery => {
  return snake<SendVideoCallLogsQuery, SendVideoCallLogsApiQuery>(sendVideoCallLogsQuery);
};

const MIN_VIDEO_CALL_TIME_IN_MIN = 20;

const sendVideoCallLogsActionHandler =
  (store: VideoCallStore) => async (roomId: string, stats?: PublisherStatContainer) => {
    try {
      let status: VideoCallStatus = VideoCallStatus.ERROR;

      const { platform, version } = getPlatformAndVersion();
      const diffTime = DateTime.now().diff(DateTime.fromJSDate(store.timeCounter.startDate), "minutes");

      if (diffTime.minutes < MIN_VIDEO_CALL_TIME_IN_MIN) status = VideoCallStatus.KO;
      if (store.errors.length === 0) status = VideoCallStatus.OK;

      const payload: VideoCallPayload = { version, platform, stats, errors: store.errors };
      const query: SendVideoCallLogsQuery = { payload, status, roomUuid: roomId };
      const sendVideoCallLogsApiQuery = transformLogsQueryToLogsQueryApi(query);

      await sendLogs(sendVideoCallLogsApiQuery);
    } catch (error) {
      log("Error sending logs", error);
    }
  };

const getPlatformAndVersion = (): Pick<VideoCallPayload, "version" | "platform"> => {
  let version: string;
  let platform: string;

  if (!isApp()) {
    platform = "web";
    version = config.webVersion!;
  } else {
    if (isIosApp()) {
      platform = "ios";
      version = config.iosAppVersion!;
    } else {
      platform = "android";
      version = config.androidAppVersion!;
    }
  }

  return { platform, version };
};

export default sendVideoCallLogsActionHandler;
